/**
 * Start-up, by role:
 *  - kiosk     a session id (from ?session= or new), the backend's domain → the pack, then the shell;
 *  - operator  the same, joining ?session= instead of starting it;
 *  - replay    the recording (?replay=), its domain → the pack, the player;
 *  - review    the gym's review items (?review=), the item's episode replayed as above, and the review panel.
 * Until the pack is known a boot screen says what it is waiting for — and why, if it fails.
 */
import { useEffect, useState } from 'react';
import { describeError, report } from '@platform/core/diagnostics';
import { newSessionId, type Flags } from '@platform/core/flags';
import { fetchItems, fetchTrajectory, type ReviewContext } from '@platform/review/gym-api';
import { withSpec } from '@platform/packs/spec-screen';
import { uiDocumentOf } from '@platform/packs/ui-spec';
import { createKiosk, type Kiosk } from '@platform/core/kiosk';
import type { BackendApi } from '@platform/core/ports';
import { createTranslator } from '@platform/i18n/translator';
import { PLATFORM_STRINGS } from '@platform/i18n/strings';
import type { KioskApp } from '@platform/react/app';
import { createReplayPlayer } from '@platform/replay/player';
import { parseRecording } from '@platform/replay/recording';
import { createHttpApi } from '@platform/transport/http-api';
import { createSseFeed } from '@platform/transport/sse-feed';
import { createUiActions } from '@platform/shell/ui-actions';
import { composeApp } from './compose';
import { packFor } from './packs';
import { createKioskVoice, avatarFor } from './voice';

export type BootState =
  | { phase: 'starting'; detail: string }
  | { phase: 'failed'; message: string; detail?: string }
  | { phase: 'ready'; app: KioskApp };

const t = createTranslator(PLATFORM_STRINGS, 'it');

/** The boot for this page's role. */
function bootFor(flags: Flags): Promise<Booted> {
  return flags.role === 'review' ? bootReview(flags) : flags.role === 'replay' ? bootReplay(flags) : bootLive(flags);
}

/**
 * The app this page shows. Only the boot still wanted becomes the app, and only it is `window.kiosk`: a boot
 * dropped while it ran (StrictMode runs the effect twice in development) may finish last, and must not take
 * `window.kiosk` from the one on screen (it did: e2e drove a disposed app under load, 19/10).
 */
export function useBoot(flags: Flags, start: (flags: Flags) => Promise<Booted> = bootFor): BootState {
  const [state, setState] = useState<BootState>({ phase: 'starting', detail: t('boot.connecting') });
  useEffect(() => {
    let disposed = false;
    let cleanup: (() => void) | null = null;
    const done = (s: BootState) => !disposed && setState(s);
    start(flags).then((r) => {
      if (disposed) {
        r.dispose?.();
        return;
      }
      const app = r.state.phase === 'ready' ? r.state.app : null;
      if (app) expose(app);
      cleanup = () => {
        if (app) unexpose(app);
        r.dispose?.();
      };
      done(r.state);
    }).catch((e) => done({ phase: 'failed', message: describeError(e) }));
    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [flags, start]);
  return state;
}

export interface Booted {
  state: BootState;
  dispose?: () => void;
}

async function bootLive(flags: Flags): Promise<Booted> {
  if (flags.role === 'operator' && !flags.sessionId) {
    return { state: { phase: 'failed', message: t('boot.operator_needs_session') } };
  }
  const sessionId = flags.sessionId ?? newSessionId();
  rememberSessionInUrl(sessionId);
  const api = createHttpApi();
  const kiosk = createKiosk({ sessionId, role: flags.role === 'operator' ? 'operator' : 'kiosk', api, feed: createSseFeed() });
  await kiosk.start();

  const pack = await waitForPack(kiosk);
  if (!pack.ok) {
    kiosk.stop();
    return { state: { phase: 'failed', message: pack.message } };
  }
  const voice = flags.role === 'kiosk' ? createKioskVoice(flags, kiosk) : null;
  const spec = uiDocumentOf(await api.ui().catch((e: unknown) => {
    report('warn', 'layout', `the screens' spec is not available: ${describeError(e)}`);
    return null;
  }) ?? pack.pack.ui);
  const { app, dispose } = composeApp({ kiosk, pack: withSpec(pack.pack, spec), flags, voice, avatar: flags.role === 'kiosk' ? avatarFor(flags, voice) : null });
  return {
    state: { phase: 'ready', app },
    dispose: () => {
      dispose();
      voice?.dispose();
      kiosk.stop();
    },
  };
}

/** The domain says which pack; without `/api/runtime`, the first session says it. */
async function waitForPack(kiosk: Kiosk): Promise<{ ok: true; pack: NonNullable<ReturnType<typeof packFor>> } | { ok: false; message: string }> {
  const deadline = Date.now() + 20_000;
  for (;;) {
    const runtime = kiosk.stores.runtime.get();
    const session = kiosk.stores.session.get();
    if (runtime?.domain) {
      const pack = packFor(runtime.domain, null);
      return pack ? { ok: true, pack } : { ok: false, message: t('boot.unknown_domain', { domain: runtime.domain }) };
    }
    if (session) {
      const pack = packFor(null, session);
      if (pack) return { ok: true, pack };
    }
    if (Date.now() > deadline) return { ok: false, message: t('boot.no_domain') };
    await new Promise((r) => setTimeout(r, 250));
  }
}

async function bootReplay(flags: Flags): Promise<Booted> {
  const id = flags.replayId!;
  const res = await fetch(`/api/replay/session/${encodeURIComponent(id)}`);
  if (!res.ok) return { state: { phase: 'failed', message: t('boot.replay_failed', { id }), detail: `HTTP ${res.status}` } };
  return playRecording(await res.text(), id, flags, null);
}

/** The gym's items for this reviewer and strategy; the one asked (or the first left), replayed. */
async function bootReview(flags: Flags): Promise<Booted> {
  const asked = flags.review!;
  const list = await fetchItems(asked.run, asked.strategy, asked.by);
  const item = list.items.find((i) => i.id === asked.item) ?? list.items[0];
  if (!item) {
    return { state: { phase: 'failed', message: `Nothing left to review in ${asked.run} for ${asked.by} (${asked.strategy}).` } };
  }
  const review: ReviewContext = { run: asked.run, by: asked.by, strategy: asked.strategy, item, list };
  return playRecording(await fetchTrajectory(asked.run, item.episode), item.episode, flags, review);
}

/** A recording's domain → the pack, a read-only kiosk fed by the player, the app. */
async function playRecording(text: string, id: string, flags: Flags, review: ReviewContext | null): Promise<Booted> {
  const recording = parseRecording(text);
  for (const s of recording.skipped) report('warn', 'replay', s);
  const firstSession = recording.steps.find((s) => s.event.kind === 'session');
  const pack = packFor(recording.run?.domain ?? null, firstSession?.event.kind === 'session' ? firstSession.event.session : null);
  if (!pack) return { state: { phase: 'failed', message: t('boot.unknown_domain', { domain: recording.run?.domain ?? '?' }) } };

  const kiosk = createKiosk({ sessionId: recording.sessionId || id, role: 'replay', api: recordingApi(recording.run), feed: null });
  await kiosk.start();
  const player = createReplayPlayer(recording, kiosk);
  // the UI the session had when it was recorded; an older recording: the domain's as the pack bundles it
  const { app, dispose } = composeApp({ kiosk, pack: withSpec(pack, uiDocumentOf(recording.ui ?? pack.ui)), flags, replay: player, review });
  return { state: { phase: 'ready', app }, dispose: () => { dispose(); player.dispose(); } };
}

/** A replay talks to no backend: the only thing it answers is the run the recording was made with. */
function recordingApi(run: Record<string, unknown> | null): BackendApi {
  const refuse = async () => { throw new Error('a replay sends nothing'); };
  return {
    resetSession: refuse, getSession: refuse, press: refuse, say: refuse, setLanguage: refuse, heartbeat: refuse, scanQr: refuse,
    announce: refuse,
    runtime: async () => (run && typeof run.domain === 'string' ? { ...run, domain: run.domain } : null),
    ui: async () => null,
  };
}

/** Keep ?session= in the address so a reload rejoins the same session. Some embedded views forbid it. */
function rememberSessionInUrl(sessionId: string): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('session') === sessionId) return;
    url.searchParams.set('session', sessionId);
    window.history.replaceState(null, '', url);
  } catch (e) {
    report('info', 'boot', `cannot write the session id in the address: ${describeError(e)}`);
  }
}

type Exposed = { kiosk?: { app: KioskApp } };

/** `window.kiosk` in the console: the app and the UI actions (`kiosk.ui.apply('text')`). */
function expose(app: KioskApp): void {
  (window as unknown as { kiosk: unknown }).kiosk = { app, ui: createUiActions(app), session: () => app.kiosk.stores.session.get() };
}

/** The app is going: `window.kiosk` stops naming it, unless another app already took it. */
function unexpose(app: KioskApp): void {
  const w = window as unknown as Exposed;
  if (w.kiosk?.app === app) delete w.kiosk;
}
